import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { USE_MOCKS, apiPatch } from '../../api/client';
import { AlertTriangle, Banknote, Building2, Check, Key, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { Tabs } from '../../components/Tabs';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { useCurrentReseller, updateReseller } from '../../api/reseller';

/** Reseller account settings — Profile, Notifications, Payout details, Security. */
export function ResellerSettings() {
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Reseller"
        title="Settings"
        subtitle="Your organization profile, notifications, payout details, and security."
      />

      <Tabs
        tabs={[
          { id: 'profile', label: 'Profile', content: <ProfileTab /> },
          { id: 'notifications', label: 'Notifications', content: <NotificationsTab /> },
          { id: 'payout', label: 'Payout details', content: <PayoutTab /> },
          { id: 'security', label: 'Security', content: <SecurityTab /> },
        ]}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */

function ProfileTab() {
  const { data: currentReseller } = useCurrentReseller();
  const [orgName, setOrgName] = useState('');
  const [address, setAddress] = useState('12 Main Rd, Sylhet');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('https://acme-resellers.com');
  const [email, setEmail] = useState('');
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  // Which DB modes this reseller offers its customers at signup. 'shared' is always
  // available (kept by the RLS self-update policy); managed/self_hosted are toggleable.
  const [allowManaged, setAllowManaged] = useState(false);
  const [allowSelfHosted, setAllowSelfHosted] = useState(false);

  useEffect(() => {
    if (!currentReseller) return;
    setOrgName(currentReseller.name);
    setPhone(currentReseller.supportPhone);
    setEmail(currentReseller.supportEmail);
    const modes = currentReseller.allowedDbModes ?? ['shared'];
    setAllowManaged(modes.includes('managed'));
    setAllowSelfHosted(modes.includes('self_hosted'));
  }, [currentReseller]);

  async function save() {
    if (!orgName.trim() || busy) return;
    setBusy(true);
    setSaved(false);
    try {
      // `name` + `allowedDbModes` map to PATCH /reseller/me; phone/website/address/support
      // email have no endpoint field yet (tracked in the integration punch-list).
      const allowedDbModes = ['shared', ...(allowManaged ? ['managed'] : []), ...(allowSelfHosted ? ['self_hosted'] : [])];
      if (!USE_MOCKS) await apiPatch('/reseller/me', { name: orgName.trim(), allowedDbModes });
      void qc.invalidateQueries({ queryKey: ['reseller', 'me'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }
  function discard() {
    if (!currentReseller) return;
    setOrgName(currentReseller.name);
    setPhone(currentReseller.supportPhone);
    setEmail(currentReseller.supportEmail);
    const modes = currentReseller.allowedDbModes ?? ['shared'];
    setAllowManaged(modes.includes('managed'));
    setAllowSelfHosted(modes.includes('self_hosted'));
  }

  if (!currentReseller) {
    return (
      <div className="px-1 py-8 space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3">
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Card className="p-5 sm:p-6">
        <CardHeader title="Organization" subtitle="Public-facing details for your reseller." />
        <div className="grid sm:grid-cols-2 gap-5">
          <Input label="Org name" required value={orgName} onChange={(e) => setOrgName(e.target.value)} />
          <Input label="Public email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <Input label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} />
          <div className="sm:col-span-2">
            <Input label="Address" value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader
          title="Reseller URL"
          subtitle="Where your customers find you online."
          action={<Badge tone="success">Active</Badge>}
        />
        <div className="flex items-center gap-3 p-3 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
          <Building2 className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
          <span className="mono text-sm text-zinc-700 dark:text-zinc-200">{currentReseller.slug}.itcitysoft.com</span>
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Database options" subtitle="Which database modes your customers can choose at signup." />
        <div className="space-y-3">
          <label className="flex items-start gap-3 opacity-70">
            <input type="checkbox" checked disabled className="mt-1 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600" />
            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Shared <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">(always available)</span>
              </div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">Runs on the shared database — fastest, no extra cost.</div>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={allowManaged}
              onChange={(e) => setAllowManaged(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600"
            />
            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Isolated (dedicated database)</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">We provision a dedicated MySQL database per customer.</div>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={allowSelfHosted}
              onChange={(e) => setAllowSelfHosted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600"
            />
            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Customer's own MySQL (BYO database)</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">Connect over TLS to a database the customer hosts.</div>
            </div>
          </label>
        </div>
      </Card>

      <div className="flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button variant="ghost" onClick={discard} disabled={busy}>Discard</Button>
        <Button onClick={save} loading={busy} disabled={busy || !orgName.trim()}>Save changes</Button>
      </div>
    </div>
  );
}

const NOTIF_GROUPS: { title: string; items: { key: string; label: string; defaultOn: boolean }[] }[] = [
  {
    title: 'Customer activity',
    items: [
      { key: 'dailyDigest', label: 'Daily digest email', defaultOn: true },
      { key: 'newSignup', label: 'New customer signup', defaultOn: true },
      { key: 'paymentFailure', label: 'Payment failure', defaultOn: true },
      { key: 'aboutToSuspend', label: 'Business about to suspend', defaultOn: true },
      { key: 'customerCancellation', label: 'Customer cancellations', defaultOn: false },
    ],
  },
  {
    title: 'Financial',
    items: [
      { key: 'payoutSent', label: 'Payout sent', defaultOn: true },
      { key: 'commissionThreshold', label: 'Commission threshold reached', defaultOn: false },
      { key: 'monthlyReport', label: 'Monthly revenue report', defaultOn: true },
    ],
  },
  {
    title: 'Operational',
    items: [
      { key: 'staffAccepted', label: 'New staff invitation accepted', defaultOn: true },
      { key: 'domainVerified', label: 'Domain verification completed', defaultOn: true },
      { key: 'brandingSaved', label: 'Branding changes saved', defaultOn: false },
    ],
  },
];

function NotificationsTab() {
  const { data: currentReseller } = useCurrentReseller();
  const qc = useQueryClient();
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const stored = currentReseller?.notifPrefs ?? {};
    const next: Record<string, boolean> = {};
    for (const g of NOTIF_GROUPS) for (const it of g.items) next[it.key] = stored[it.key] ?? it.defaultOn;
    setPrefs(next);
  }, [currentReseller]);

  async function save() {
    if (busy) return;
    setBusy(true);
    setSaved(false);
    try {
      await updateReseller({ notifPrefs: prefs });
      void qc.invalidateQueries({ queryKey: ['reseller', 'me'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      {NOTIF_GROUPS.map((g) => (
        <div key={g.title}>
          <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">{g.title}</div>
          <Card className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {g.items.map((it) => (
              <ToggleRow
                key={it.key}
                label={it.label}
                on={prefs[it.key] ?? it.defaultOn}
                onToggle={(v) => setPrefs((p) => ({ ...p, [it.key]: v }))}
              />
            ))}
          </Card>
        </div>
      ))}

      <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
          Required (cannot opt out)
        </div>
        <ul className="space-y-1.5 text-sm text-zinc-700 dark:text-zinc-200">
          <li className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 text-zinc-400" /> Account security alerts
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 text-zinc-400" /> Legal / Terms updates
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 text-zinc-400" /> Refund / chargeback notices
          </li>
        </ul>
      </div>

      <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button onClick={save} loading={busy} disabled={busy}>Save changes</Button>
      </div>
    </div>
  );
}

function PayoutTab() {
  const { data: currentReseller } = useCurrentReseller();
  const qc = useQueryClient();
  const [bankName, setBankName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [routing, setRouting] = useState('');
  const [bkash, setBkash] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const p = currentReseller?.payout;
    setBankName(p?.bankName ?? '');
    setAccountName(p?.accountName ?? '');
    setAccountNumber(p?.accountNumber ?? '');
    setRouting(p?.routingNumber ?? '');
    setBkash(p?.bkash ?? '');
  }, [currentReseller]);

  function discard() {
    const p = currentReseller?.payout;
    setBankName(p?.bankName ?? '');
    setAccountName(p?.accountName ?? '');
    setAccountNumber(p?.accountNumber ?? '');
    setRouting(p?.routingNumber ?? '');
    setBkash(p?.bkash ?? '');
  }

  async function save() {
    if (busy) return;
    setBusy(true);
    setSaved(false);
    try {
      await updateReseller({ payout: { bankName, accountName, accountNumber, routingNumber: routing, bkash } });
      void qc.invalidateQueries({ queryKey: ['reseller', 'me'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }

  const verified = currentReseller?.payoutVerified ?? false;

  return (
    <div className="space-y-5">
      <Card className="p-5 sm:p-6">
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Banknote className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
              Bank account
            </span>
          }
          subtitle="Where your monthly commission is settled."
          action={<Badge tone={verified ? 'success' : 'warning'}>{verified ? 'Verified' : 'Pending verification'}</Badge>}
        />
        <div className="grid sm:grid-cols-2 gap-5">
          <Input label="Bank name" value={bankName} onChange={(e) => setBankName(e.target.value)} />
          <Input label="Account name" value={accountName} onChange={(e) => setAccountName(e.target.value)} />
          <Input label="Account number" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
          <Input label="Routing number" value={routing} onChange={(e) => setRouting(e.target.value)} />
        </div>
        <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
          Editing these details requires re-verification by our team before the next payout.
        </p>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="bKash (alternative)" subtitle="Optional fallback for faster payouts." />
        <Input label="bKash personal number" value={bkash} onChange={(e) => setBkash(e.target.value)} />
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Payout schedule" />
        <div className="rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 p-4 text-sm">
          <p className="text-zinc-700 dark:text-zinc-200">
            Payouts run on the <span className="font-medium">15th of each month</span> for the previous month's
            commission, with a minimum threshold of ৳5,000.
          </p>
        </div>
      </Card>

      <div className="flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button variant="ghost" onClick={discard} disabled={busy}>Discard</Button>
        <Button onClick={save} loading={busy} disabled={busy}>Save changes</Button>
      </div>
    </div>
  );
}

function SecurityTab() {
  return (
    <div className="space-y-4">
      <Card className="p-5 sm:p-6">
        <CardHeader
          title="Password"
          subtitle="Last changed 03 Mar 2026"
          action={
            <Button variant="secondary" size="sm">
              Change password
            </Button>
          }
        />
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              Two-factor authentication
              <Badge tone="success">Enabled</Badge>
            </span>
          }
          subtitle="TOTP authenticator app · 6 backup codes remaining"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" iconLeft={<Key className="w-4 h-4" />}>
            Regenerate recovery codes
          </Button>
          <Button variant="ghost" size="sm">
            Disable 2FA
          </Button>
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="API keys" subtitle="For programmatic access to your reseller data." />
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm">
            Manage keys
          </Button>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">2 keys active</span>
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Danger zone" />
        <div className="rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-rose-900 dark:text-rose-200">Delete reseller account</p>
            <p className="text-sm text-rose-800 dark:text-rose-300 mt-0.5">
              All customers will be migrated to platform-direct billing first. 30-day grace period before deletion.
            </p>
          </div>
          <Button variant="destructive" size="sm">
            Delete
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ToggleRow({ label, on, onToggle }: { label: string; on: boolean; onToggle: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-4 px-5 sm:px-6 py-3 cursor-pointer">
      <span className="text-sm text-zinc-800 dark:text-zinc-100">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => onToggle(!on)}
        className={cn(
          'relative w-9 h-5 rounded-full transition-colors',
          on ? 'bg-brand dark:bg-brand-dm' : 'bg-zinc-200 dark:bg-zinc-700',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform',
            on ? 'translate-x-[18px]' : 'translate-x-0.5',
          )}
        />
      </button>
    </label>
  );
}
